import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import i18n from '../../i18n';
import { currencies, currenciesMap } from '../constants/currency';
import agent from '../api/agent';
import { CountriesResponse } from '../models/countriesModel';
import { Language } from '../models/language';
import { Measure } from '../utils/parcel-measurements.ts/parcel-measurements-utils';
import {
  CountryLimit,
  PriorityCountries,
} from '../features/main/countries-list/countries.config';
import { getLocalizations, getLocalizationsMap } from '../constants/languages';
import { handleError } from '../utils/generic/generic.utils';

export const DEFAULT_COUNTRY: CountriesResponse = {
  administrative_units: {
    allowed_values: [],
    form_field: '',
    required: false,
    placeholder: '',
    selected_value: '',
    title: '',
    validation: [],
    frontend_validation: [],
  },
  country_code: 'US',
  alias: 'us',
  local_name: 'United States of America',
  default_measures: Measure.LB_IN,
  default_language: 'en',
  en_name: 'United States of America',
  flag_emoji: '🇺🇸',
  flag_emoji_2: '🇺🇸',
  flag_image: '',
  flag_image_svg: '',
  continent: 'North America',
  allowed_languages: ['en', 'uk', 'ru'],
  allowed_currencies: ['USD', 'CAD', 'UAH'],
  default_currency: 'USD',
  links: {
    privacy_policy: '',
    terms_of_use: '',
    delivery_rules: '',
  },
  support: [],
  settings: [],
  countryDetails: {
    code: 'US',
    emoji: '🇺🇸',
    dial_code: '+1',
    localized_name: 'USA',
  },
  localized_names: [
    {
      lang: 'en',
      value: 'USA',
    },
    {
      lang: 'uk',
      value: '\u0421\u0428\u0410',
    },
    {
      lang: 'ru',
      value: '\u0421\u0428\u0410',
    },
    {
      lang: 'it',
      value: 'Stati Uniti',
    },
    {
      lang: 'pl',
      value: 'USA',
    },
  ],
};

const EN_LOCALIZATION_INDEX = 0;

export const getBrowserLocalization = (availableLocalizations: Language[]) => {
  const browserLanguageCode = navigator.language.split('-')[0];
  const foundLanguage = availableLocalizations.find(
    (loc) => loc.code === browserLanguageCode
  );
  return foundLanguage ?? availableLocalizations[EN_LOCALIZATION_INDEX];
};

export default class LocalizationsStore {
  readonly hydrationPromises: Promise<any>[] = [];

  availableCountries: CountriesResponse[] | undefined = undefined;

  selectedCountry: CountriesResponse | undefined = undefined;

  // Preferred locale is used when user opens root page without specifying locale in the URL
  preferredCountryAliasCode: string | undefined = undefined;
  preferredLanguageCode: string | undefined = undefined;

  availableLocalizations: Language[] = getLocalizations(
    this.selectedCountry?.country_code ?? DEFAULT_COUNTRY.country_code
  );

  isLoadingCountriesApi: boolean = false;

  isLanguageChanging: boolean = false;

  availableCurrencies = currencies;

  selectedLocalization: Language | undefined = undefined;

  countryLimit: CountryLimit | null = null;

  get languageCode() {
    return (
      this.selectedLocalization?.code.toLowerCase() ||
      this.preferredLanguageCode ||
      getBrowserLocalization(this.availableLocalizations).code.toLowerCase()
    );
  }

  get localeParam() {
    const countryCode =
      this.selectedCountry?.alias.toLowerCase() ||
      this.preferredCountryAliasCode?.toLowerCase() ||
      DEFAULT_COUNTRY.alias.toLowerCase();

    return `/${countryCode}-${this.languageCode}`;
  }

  get preferredCountry() {
    return this.availableCountries?.find(
      (x) =>
        x.alias.toLocaleLowerCase() ===
        this.preferredCountryAliasCode?.toLocaleLowerCase()
    );
  }

  constructor() {
    makeAutoObservable(this);
    this.hydrationPromises.push(
      makePersistable(this, {
        name: 'LocalizationsStore-session',
        properties: [
          'isLoadingCountriesApi',
          'isLanguageChanging',
          'availableCurrencies',
          'availableCountries',
          'availableLocalizations',
          'countryLimit',
        ],
        storage: window.sessionStorage,
      })
    );
    this.hydrationPromises.push(
      makePersistable(this, {
        name: 'LocalizationStore-local',
        properties: ['preferredCountryAliasCode', 'preferredLanguageCode'],
        storage: window.localStorage,
      })
    );
  }

  setAvailableLocalizations = (countryCode: string, langs: string[]) => {
    const localizationMap = getLocalizationsMap(countryCode);
    this.availableLocalizations = langs
      .map((lang) => localizationMap[lang])
      .filter(Boolean);
  };

  setAvailableCurrencies = (currs: string[]) => {
    this.availableCurrencies = currs
      .map((curr) => currenciesMap[curr])
      .filter(Boolean);
  };

  private selectLocalization = async (language: Language) => {
    await i18n.changeLanguage(language.code);
    if (
      language.code === this.selectedLocalization?.code &&
      language.flagEmoji === this.selectedLocalization?.flagEmoji
    ) {
      return;
    }

    runInAction(async () => {
      // this.fetchCountries(true);
      this.selectedLocalization = language;
    });
  };

  selectLocalizationByCode = (code: string) => {
    const foundLanguage = this.availableLocalizations.find(
      (loc) => loc.code === code
    );
    if (foundLanguage) {
      this.selectLocalization({ ...foundLanguage });
      return true;
    }

    return false;
  };

  // aliasCode is also country code, but lowercase and may differ from official country code (e.g. GB is country code -> uk is alias). Used for marketing reasons.
  selectCountryByCode = (aliasCode: string) => {
    if (!this.availableCountries) return;

    const foundCountry =
      this.availableCountries.find((c) => c.alias === aliasCode) ??
      this.availableCountries.find((c) => c.alias === DEFAULT_COUNTRY.alias);

    if (!foundCountry) return;

    runInAction(() => {
      this.selectedCountry = foundCountry;

      this.setAvailableCurrencies(foundCountry.allowed_currencies);

      this.setAvailableLocalizations(
        foundCountry.country_code,
        foundCountry.allowed_languages
      );
    });

    return foundCountry;
  };

  fetchCountries = async (rewriteExisting?: boolean) => {
    if (this.availableCountries && !rewriteExisting) return;

    try {
      this.isLoadingCountriesApi = true;
      const apiCountries = await agent.Countries.countries();

      runInAction(() => {
        this.availableCountries = apiCountries;
      });
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoadingCountriesApi = false;
    }
  };

  findCountryByCode = (countryCode: string) => {
    if (!this.availableCountries) return undefined;

    return this.availableCountries.find(
      (i) => i.country_code === countryCode.toLocaleUpperCase()
    );
  };

  setCountryLimit = (code: string) => {
    const limit = PriorityCountries[code];
    runInAction(() => {
      this.countryLimit = limit;
    });
  };

  setIsLanguageChanging = (isChanging: boolean) => {
    this.isLanguageChanging = isChanging;
  };

  selectLocale = (country: CountriesResponse | undefined) => {
    if (!country) return;

    this.selectCountryByCode(country.alias);

    // First try to pick preferredLang from local storage if available
    if (
      this.preferredLanguageCode &&
      country.allowed_languages.includes(this.preferredLanguageCode) &&
      this.selectLocalizationByCode(this.preferredLanguageCode)
    ) {
      return;
    }

    // If not #1 - try pick first found language from supported by browser + by app + by country
    const browserLanguages = navigator.languages.map((x) => x.split('-')[0]);

    for (let index = 0; index < browserLanguages.length; index++) {
      const lang = browserLanguages[index];

      if (
        country.allowed_languages.includes(lang) &&
        this.availableLocalizations.find((x) => x.code === lang) &&
        this.selectLocalizationByCode(lang)
      ) {
        return;
      }
    }

    // If not #2 - pick en or any from available
    this.selectLocalizationByCode(
      this.availableLocalizations.find((x) => x.code === 'en')?.code ||
        this.availableLocalizations[0].code
    );
  };

  setPreferredCountryAliasCode = (alisaCode: string | undefined) => {
    this.preferredCountryAliasCode = alisaCode;
  };

  setPreferredLanguageCode = (lang: string | undefined) => {
    this.preferredLanguageCode = lang;
  };

  getCountryLocalizedTitleByCode = (
    code: string | undefined,
    includeFlag: boolean = false,
    lang: string | undefined = this.selectedLocalization?.code
  ) => {
    const country = this.availableCountries?.find(
      (x) => x.country_code === code
    );
    return this.getCountryLocalizedTitle(country, includeFlag, lang);
  };

  getCountryLocalizedTitle = (
    country: CountriesResponse | undefined,
    includeFlag: boolean = false,
    lang: string | undefined = this.selectedLocalization?.code
  ) => {
    if (!country) return '';
    const localizedName =
      country.localized_names.find((y) => y.lang === lang)?.value ??
      country.countryDetails.localized_name;
    return `${includeFlag ? country.flag_emoji_2 : ''} ${localizedName}`.trim();
  };
}
